import React, { Component } from "react";

import AppContext from "./AppContext";
import config from "../components/App/config";
import {
  buildAircraftsObject,
  buildAirlinesObject,
  buildAirportsObject,
  buildCountriesObject,
  buildPlacesObject,
  buildTripsObject,
  calculateVisibility,
  generateEmptyYears,
  getContextValues
} from "./functions";
import {
  handleFetchAircrafts,
  handleFetchAirlines,
  handleFetchAirports,
  handleFetchCountries,
  handleFetchPlaces,
  handleFetchTrips
} from "./fetchUtils";

class GlobalState extends Component<any, any> {
  constructor(props: any) {
    super(props);

    this.state = {
      isSmallScreen: false,
      isMenuShown: config.isMenuShown,
      selectedMenu: config.selectedMenu,
      infoWindow: {
        isVisible: false,
        type: undefined,
        id: undefined
      },
      airlines: {},
      aircrafts: {},
      countries: [],
      airports: [],
      places: [],
      trips: [],
      routes: [],
      years: generateEmptyYears(),
      layers: config.layers,
      modes: config.modes
    };
  }

  componentDidMount() {
    this.checkSmallScreen();
    this.fetchInitData();
  }

  checkSmallScreen = () => {
    const width = window.innerWidth;
    if (width < config.smallScreenTreshold)
      this.setState({
        isSmallScreen: true
      });
  };

  fetchInitData = (): void => {
    this.fetchAirlines();
  };

  handleFetchFail = () => {
    alert(
      "Pobranie danych zakończone niepowodzeniem. Być może odświeżenie strony pomoże."
    );
  };

  fetchAirlines = () => {
    handleFetchAirlines({
      injectAirlines: this.injectAirlines,
      fetchAircrafts: this.fetchAircrafts,
      handleFetchFail: this.handleFetchFail
    });
  };

  injectAirlines = (json: any) => {
    const airlines = buildAirlinesObject(json.airlines);
    this.setState({
      airlines
    });
  };

  fetchAircrafts = () => {
    handleFetchAircrafts({
      injectAircrafts: this.injectAircrafts,
      fetchCountries: this.fetchCountries,
      handleFetchFail: this.handleFetchFail
    });
  };

  injectAircrafts = (json: any) => {
    const aircrafts = buildAircraftsObject(json.aircrafts);
    this.setState({
      aircrafts
    });
  };

  fetchCountries = () => {
    handleFetchCountries({
      injectCountries: this.injectCountries,
      fetchAirports: this.fetchAirports,
      handleFetchFail: this.handleFetchFail
    });
  };

  injectCountries = (json: any) => {
    const countries = buildCountriesObject(json.countries);
    this.setState({
      countries
    });
  };

  fetchAirports = () => {
    handleFetchAirports({
      injectAirports: this.injectAirports,
      fetchPlaces: this.fetchPlaces,
      handleFetchFail: this.handleFetchFail
    });
  };

  injectAirports = (json: any) => {
    const airports = buildAirportsObject(json.airports);
    this.setState({ airports });
  };

  fetchPlaces = () => {
    handleFetchPlaces({
      injectPlaces: this.injectPlaces,
      fetchTrips: this.fetchTrips,
      handleFetchFail: this.handleFetchFail
    });
  };

  injectPlaces = (json: any) => {
    const places = buildPlacesObject(json.places);
    this.setState({ places });
  };

  fetchTrips = () => {
    handleFetchTrips({
      injectTrips: this.injectTrips,
      handleFetchFail: this.handleFetchFail
    });
  };

  injectTrips = (json: any) => {
    const {
      years,
      airports,
      countries,
      places,
      routes,
      airlines,
      aircrafts
    } = this.state;
    const injectedState = buildTripsObject(json.trips, {
      years,
      airports,
      countries,
      places,
      routes,
      airlines,
      aircrafts
    });
    this.setState({
      ...injectedState
    });
  };

  toggleShowMenu = () => {
    if (this.state.isSmallScreen) {
      // With small screen first click should show menu
      // This is because isMenuShown is undefined by default
      this.setState({
        isMenuShown: this.state.isMenuShown === true ? false : true
      });
    } else {
      this.setState({
        // With bigger screen first click should hide menu
        // This is because isMenuShown is undefined by default
        isMenuShown: this.state.isMenuShown === false ? true : false
      });
    }
  };

  selectMenu = (menuId: string) => {
    this.setState({
      selectedMenu: menuId
    });
  };

  toggleCountrySelection = (id: string) => {
    const {
      places,
      airports,
      routes,
      layers,
      years,
      trips,
      modes
    } = this.state;

    const countries = this.state.countries.map((country: any) => {
      if (country.id === id)
        return {
          ...country,
          isVisible: !country.isVisible
        };

      return country;
    });

    const state = calculateVisibility({
      places,
      airports,
      routes,
      countries,
      layers,
      years,
      trips,
      modes
    });

    this.setState({ ...state });
  };

  showAllCountries = () => {
    const {
      places,
      airports,
      routes,
      layers,
      years,
      trips,
      modes
    } = this.state;

    const countries = this.state.countries.map((country: any) => {
      return { ...country, isVisible: true };
    });

    const state = calculateVisibility({
      places,
      airports,
      routes,
      countries,
      layers,
      years,
      trips,
      modes
    });

    this.setState({
      ...state
    });
  };

  hideAllCountries = () => {
    const {
      places,
      airports,
      routes,
      layers,
      years,
      trips,
      modes
    } = this.state;

    const countries = this.state.countries.map((country: any) => {
      return { ...country, isVisible: false };
    });

    const state = calculateVisibility({
      places,
      airports,
      routes,
      countries,
      layers,
      years,
      trips,
      modes
    });

    this.setState({ ...state });
  };

  toggleYearSelection = (id: string) => {
    const {
      places,
      airports,
      routes,
      countries,
      layers,
      trips,
      modes
    } = this.state;

    const years = this.state.years.map((year: any) => {
      if (year.id === id)
        return {
          ...year,
          isVisible: !year.isVisible
        };

      return year;
    });

    const state = calculateVisibility({
      places,
      airports,
      routes,
      countries,
      layers,
      years,
      trips,
      modes
    });

    this.setState({ ...state, years });
  };

  showAllYears = () => {
    const {
      places,
      airports,
      routes,
      countries,
      layers,
      trips,
      modes
    } = this.state;

    const years = this.state.years.map((year: any) => {
      return { ...year, isVisible: true };
    });

    const state = calculateVisibility({
      places,
      airports,
      routes,
      countries,
      layers,
      years,
      trips,
      modes
    });

    this.setState({
      ...state,
      years
    });
  };

  hideAllYears = () => {
    const {
      places,
      airports,
      routes,
      countries,
      layers,
      trips,
      modes
    } = this.state;

    const years = this.state.years.map((year: any) => {
      return { ...year, isVisible: false };
    });

    const state = calculateVisibility({
      places,
      airports,
      routes,
      countries,
      layers,
      years,
      trips,
      modes
    });

    this.setState({ ...state, years });
  };

  changeMode = (id: string) => {
    const {
      places,
      airports,
      routes,
      countries,
      layers,
      trips,
      years
    } = this.state;

    const modes = this.state.modes.map((mode: any) => {
      if (mode.id === id) {
        return {
          ...mode,
          isSelected: true
        };
      }
      return {
        ...mode,
        isSelected: false
      };
    });

    const state = calculateVisibility({
      places,
      airports,
      routes,
      countries,
      layers,
      years,
      trips,
      modes
    });

    this.setState({ ...state, modes });
  };

  toggleLayerSelection = (id: string) => {
    const {
      places,
      airports,
      routes,
      countries,
      years,
      trips,
      modes
    } = this.state;

    const layers = this.state.layers.map((layer: any) => {
      if (layer.id === id)
        return {
          ...layer,
          isVisible: !layer.isVisible
        };

      return layer;
    });

    const state = calculateVisibility({
      places,
      airports,
      routes,
      countries,
      layers,
      years,
      trips,
      modes
    });

    this.setState({ ...state, layers });
  };

  showAllLayers = () => {
    const {
      places,
      airports,
      routes,
      countries,
      years,
      trips,
      modes
    } = this.state;

    const layers = this.state.layers.map((layer: any) => {
      return { ...layer, isVisible: true };
    });

    const state = calculateVisibility({
      places,
      airports,
      routes,
      countries,
      layers,
      years,
      trips,
      modes
    });

    this.setState({
      ...state,
      layers
    });
  };

  hideAllLayers = () => {
    const {
      places,
      airports,
      routes,
      countries,
      years,
      trips,
      modes
    } = this.state;

    const layers = this.state.layers.map((layer: any) => {
      return { ...layer, isVisible: false };
    });

    const state = calculateVisibility({
      places,
      airports,
      routes,
      countries,
      layers,
      years,
      trips,
      modes
    });

    this.setState({ ...state, layers });
  };

  toggleTripSelection = (id: number) => {
    const {
      places,
      airports,
      routes,
      countries,
      years,
      layers,
      modes
    } = this.state;

    const trips = this.state.trips.map((trip: any) => {
      if (trip.id === id)
        return {
          ...trip,
          isVisible: !trip.isVisible
        };

      return trip;
    });

    const state = calculateVisibility({
      places,
      airports,
      routes,
      countries,
      layers,
      years,
      trips,
      modes
    });

    this.setState({ ...state });
  };

  showAllTrips = () => {
    const {
      places,
      airports,
      routes,
      countries,
      years,
      layers,
      modes
    } = this.state;

    const trips = this.state.trips.map((trip: any) => {
      return { ...trip, isVisible: true };
    });

    const state = calculateVisibility({
      places,
      airports,
      routes,
      countries,
      layers,
      years,
      trips,
      modes
    });

    this.setState({
      ...state
    });
  };

  hideAllTrips = () => {
    const {
      places,
      airports,
      routes,
      countries,
      years,
      layers,
      modes
    } = this.state;

    const trips = this.state.trips.map((trip: any) => {
      return { ...trip, isVisible: false };
    });

    const state = calculateVisibility({
      places,
      airports,
      routes,
      countries,
      layers,
      years,
      trips,
      modes
    });

    this.setState({ ...state });
  };

  showInfoWindow = (type: string, id: string) => {
    this.setState({
      infoWindow: {
        isVisible: true,
        type,
        id
      }
    });
  };

  hideInfoWindow = () => {
    this.setState({
      infoWindow: {
        isVisible: false,
        type: undefined,
        id: undefined
      }
    });
  };

  render() {
    const contextMethods = {
      toggleShowMenu: this.toggleShowMenu,
      selectMenu: this.selectMenu,
      toggleCountrySelection: this.toggleCountrySelection,
      showAllCountries: this.showAllCountries,
      hideAllCountries: this.hideAllCountries,
      toggleYearSelection: this.toggleYearSelection,
      showAllYears: this.showAllYears,
      hideAllYears: this.hideAllYears,
      changeMode: this.changeMode,
      toggleLayerSelection: this.toggleLayerSelection,
      showAllLayers: this.showAllLayers,
      hideAllLayers: this.hideAllLayers,
      toggleTripSelection: this.toggleTripSelection,
      showAllTrips: this.showAllTrips,
      hideAllTrips: this.hideAllTrips,
      showInfoWindow: this.showInfoWindow,
      hideInfoWindow: this.hideInfoWindow
    };

    const contextValue = getContextValues(this.state, contextMethods);

    return (
      <AppContext.Provider value={contextValue}>
        {this.props.children}
      </AppContext.Provider>
    );
  }
}

export default GlobalState;
